import {useContext, useEffect, useState} from 'react'
import {ModuleContext, SELECT_MODULE} from '../context/context'
import SelectInput from '../../../common/SelectInput'
import InputText from '../../../common/InputText'
import Button from '../../../common/Button'
import useDebouncedValue from '../../../../utils/common_function'
import {useParams} from 'react-router-dom'
import {getRequest, postRequest} from '../../../../services/base'
import {NavContext} from '../../../../context/global_context'
import {Col, Flex, Form, Modal, Row, Tooltip, Typography} from 'antd'
import {useIntl} from 'react-intl'

const ModuleFormModal = (props) => {
  const intl = useIntl()
  let { vehicle } = useParams()

  const [state, dispatch] = useContext(ModuleContext)
  const [, navDispatch] = useContext(NavContext)
  const [searchFailed, setSearchFailed] = useState(false)

  const [form] = Form.useForm()

  const [connectorOptions, setConnectorOptions] = useState([])
  const [connectorLoading, setConnectorLoading] = useState(false)
  const [searchConnector, setSearchConnector] = useState('')
  const debouncedConnector = useDebouncedValue(searchConnector, 500)

  useEffect(() => {
    if (debouncedConnector !== undefined && state.show_module_popup)
      fetchConnectorOptions(debouncedConnector)
  }, [debouncedConnector])

  const fetchConnectorOptions = async (search = '') => {
    setConnectorLoading(true)
    try {
      let res = await getRequest(
        `/api/connector/?vehicle=${vehicle}&search=${search}`
      )
      // Search-as-you-type: shown in the dropdown, not as a toast per keystroke.
      if (res?.status === false) {
        setSearchFailed(true)
        setConnectorOptions([])
        return []
      }
      setSearchFailed(false)
      const options = (res.results || []).map((item) => ({
        value: item.name,
        label: item.name,
        ...item,
      }))
      setConnectorOptions(options)
      return options
    } finally {
      setConnectorLoading(false)
    }
  }

  const closeFormPopup = () => {
    dispatch({
      type: SELECT_MODULE,
      data: { selected_module: {}, show_module_popup: false },
    })
    form.resetFields()
  }

  const saveModule = async (values) => {
    const formData = new FormData()
    Object.entries(values).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        // Multi-select: send each selected value (could be plain IDs or option objects)
        value.forEach((item) => {
          if (item && typeof item === 'object' && item.value !== undefined) {
            formData.append(key, item.value)
          } else if (item !== undefined && item !== null) {
            formData.append(key, item)
          }
        })
      } else if (
        value &&
        typeof value === 'object' &&
        value.value !== undefined
      ) {
        formData.append(key, value.value)
      } else if (value !== undefined && value !== null) {
        formData.append(key, value)
      }
    })

    let res = await postRequest(
      `/api/modules/save_modules/${vehicle}/`,
      formData
    )

    if (res?.status) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'modules.saveSuccess' }),
        },
      })
      props.getListData()
      closeFormPopup()
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'modules.saveError' }),
        },
      })
    }
  }

  // Initialize form when modal opens: fetch connector options first, then set values
  useEffect(() => {
    if (!state.show_module_popup) return
    const initForm = async () => {
      await fetchConnectorOptions('')
      const mod = state?.selected_module || {}
      form.setFieldsValue({
        acronym: mod.acronym || null,
        module_id: mod.module_id || null,
        connectors: (mod.connector || []).map((c) => ({
          value: c.name,
          label: c.name,
        })),
      })
    }
    initForm()
  }, [state.show_module_popup])

  return (
    <Modal
      open={state.show_module_popup}
      title={intl.formatMessage({ id: 'modules.moduleDetails' })}
      width="50%"
      className="modules-form-modal"
      centered={true}
      footer={null}
      closable={true}
      onCancel={closeFormPopup}
    >
      <div className="container">
        <Form
          id="module-form"
          name="module-form"
          form={form}
          layout="vertical"
          onFinish={saveModule}
        >
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <InputText
                name="acronym"
                label={intl.formatMessage({ id: 'modules.acronym' })}
                errorHelp={true}
                rules={[
                  { required: true, message: '' },
                  {
                    pattern: /^\S+$/u,
                    message: intl.formatMessage(
                      { id: 'validation.noSpaces' },
                      { label: intl.formatMessage({ id: 'modules.acronym' }) }
                    ),
                  },
                ]}
              />
            </Col>

            <Col span={12}>
              <InputText
                name="module_id"
                label={intl.formatMessage({ id: 'modules.moduleId' })}
                isRequired={false}
              />
            </Col>

            <Col span={24}>
              <SelectInput
                name="connectors"
                label={intl.formatMessage({ id: 'modules.connectorId' })}
                options={connectorOptions}
                notFoundContent={
                  searchFailed ? (
                    <Typography.Text type="danger" style={{ color: 'var(--color-form-link-danger)' }}>
                      {intl.formatMessage({ id: 'generic.searchFailed' })}
                    </Typography.Text>
                  ) : undefined
                }
                placeholder={intl.formatMessage({
                  id: 'modules.selectConnector',
                })}
                isRequired={false}
                isMulti={true}
                allowClear={true}
                showSearch={true}
                showLoader={connectorLoading}
                onInputChange={(value) => {
                  setSearchConnector(value || '')
                }}
                maxTagCount="responsive"
                maxTagPlaceholder={(omittedValues) => (
                  <Tooltip title={omittedValues.map((v) => v.label).join(', ')}>
                    <span>+{omittedValues.length}</span>
                  </Tooltip>
                )}
              />
            </Col>

            <Col span={24}>
              <Flex justify="end" className="py-4">
                <Button htmlType="submit" type="primary">
                  {intl.formatMessage({ id: 'modules.save' })}
                </Button>
              </Flex>
            </Col>
          </Row>
        </Form>
      </div>
    </Modal>
  )
}

export default ModuleFormModal
